/**
 * Forced password change — blocking modal for first login on an
 * admin-created LOCAL account (external agents, contractors).
 *
 * The admin picks a temporary password and emails it; `must_change_password`
 * on the user forces this before the app shell is usable. Not dismissable:
 * no backdrop click, no Esc, no close button — the only ways out are setting
 * a password or signing out.
 *
 * Usage (after auth, before rendering the shell):
 *   import { maybeForcePasswordChange } from '@isg/ui-kit';
 *   await maybeForcePasswordChange();
 */
import { store } from '../state/store.js';
import { auth } from '../services/auth.js';
import { t } from '../i18n/index.js';

function esc(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

export function maybeForcePasswordChange() {
  const user = store.get('user') || {};
  const forced = store.get('must_change_password') || user.must_change_password;
  if (!forced) return Promise.resolve(false);

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    // class deliberately NOT 'modal-overlay' — the global Esc-dismiss
    // handler must never close this one.
    overlay.className = 'force-pw-overlay';
    overlay.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,0.75);'
      + 'z-index:10000;display:flex;align-items:center;justify-content:center;padding:1rem;';
    overlay.innerHTML = `
      <div class="card" style="width:100%;max-width:420px;background:var(--white,#fff);">
        <div style="padding:var(--space-5) var(--space-5) 0;">
          <h2 style="margin:0 0 var(--space-2);font-size:1.125rem;font-weight:700;">${esc(t('password.title'))}</h2>
          <p style="margin:0 0 var(--space-4);font-size:var(--text-sm);color:var(--text-secondary);line-height:1.5;">
            ${esc(t('password.intro'))}
          </p>
        </div>
        <div style="padding:0 var(--space-5) var(--space-5);display:flex;flex-direction:column;gap:var(--space-3);">
          <div class="form-group" style="margin:0">
            <label class="form-label">${esc(t('password.temporary'))}</label>
            <input type="password" id="fpw-current" class="form-input" autocomplete="current-password">
          </div>
          <div class="form-group" style="margin:0">
            <label class="form-label">${esc(t('password.new'))}</label>
            <input type="password" id="fpw-new" class="form-input" autocomplete="new-password">
          </div>
          <div class="form-group" style="margin:0">
            <label class="form-label">${esc(t('password.confirm'))}</label>
            <input type="password" id="fpw-confirm" class="form-input" autocomplete="new-password">
          </div>
          <div id="fpw-error" style="display:none;font-size:var(--text-sm);color:var(--red-600);"></div>
          <button class="btn btn-primary" id="fpw-save">${esc(t('password.submit'))}</button>
          <button class="btn btn-ghost btn-sm" id="fpw-logout" style="color:var(--text-tertiary)">${esc(t('password.sign_out'))}</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    const errEl = overlay.querySelector('#fpw-error');
    const saveBtn = overlay.querySelector('#fpw-save');
    const showError = (msg) => {
      errEl.textContent = msg;
      errEl.style.display = 'block';
    };

    overlay.querySelector('#fpw-current').focus();

    overlay.querySelector('#fpw-logout').addEventListener('click', () => auth.logout());

    saveBtn.addEventListener('click', async () => {
      const current = overlay.querySelector('#fpw-current').value;
      const next = overlay.querySelector('#fpw-new').value;
      const confirm = overlay.querySelector('#fpw-confirm').value;
      errEl.style.display = 'none';

      if (!current || !next) return showError(t('password.fill_every_field'));
      if (next !== confirm) return showError(t('password.mismatch'));

      saveBtn.disabled = true;
      saveBtn.textContent = t('common.saving');
      try {
        await auth.changePassword(current, next);
        const u = store.get('user');
        if (u) store.set('user', { ...u, must_change_password: false });
        overlay.remove();
        resolve(true);
      } catch (err) {
        showError(err.message || t('password.failed'));
        saveBtn.disabled = false;
        saveBtn.textContent = t('password.submit');
      }
    });

    overlay.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') saveBtn.click();
    });
  });
}
